import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Clock, DoorOpen, MapPin, PackageCheck, Truck } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { inkOf, themeOf, tint } from "@/lib/mall";
import Billboard from "@/components/mall/Billboard";
import { useFeature } from "@/context/SiteModeContext";
import MessageShop from "@/components/mall/MessageShop";
import StoreReviews from "@/components/mall/StoreReviews";
import { ServiceGrid } from "@/components/mall/ServiceGrid";
import { GhostButton } from "@/components/Buttons";
import { PoweredBy } from "@/components/mall/solo/PoweredBy";

function Chip({ icon: Icon, children, color, testid }) {
    if (!children) return null;
    return (
        <span data-testid={testid}
            className="inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-600"
            style={{ borderColor: tint(color, 0.4), background: tint(color, 0.06) }}>
            <Icon size={11} className="shrink-0" /> <span className="truncate">{children}</span>
        </span>
    );
}

/**
 * ONE home page per shop, short and decision-first: a compact hero, the shop's own promotional
 * billboard, then one card per service. Click a card and you are on that service's page, which
 * opens with its own banner. Policies live on /stores/<slug>/policies, linked from the footer.
 */
export function ShopHome({ site, directory: inDirectory = false, onBack }) {
    const directoryOn = useFeature("directory");
    const directory = inDirectory && directoryOn;
    const store = site.store;
    const [boards, setBoards] = useState(site.billboard ? [site.billboard] : []);
    const [rev, setRev] = useState(null);
    const billboardsOn = useFeature("billboards");

    useEffect(() => {
        if (billboardsOn) api.get(`/billboards/store/${store.slug}`).then(({ data }) => { if (data.billboards?.length) setBoards(data.billboards); }).catch(() => { });
        api.get(`/reviews/store/${store.slug}`).then(({ data }) => setRev(data)).catch(() => setRev(null));
    }, [store.slug, billboardsOn]);

    const theme = themeOf(store);
    const color = theme.primary;
    const ink = inkOf(color);
    const ops = store.ops || {};
    const q = `?store=${store.slug}`;

    return (
        <>
            <section className="relative bg-white" data-testid="store-hero">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px"
                    style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
                <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    className="mx-auto max-w-7xl px-4 py-8 sm:px-6 md:py-10">
                    <div className="flex flex-wrap items-center gap-3">
                        {directory && (
                            <button onClick={onBack} data-testid="store-back-to-mall"
                                className="inline-flex items-center gap-2 rounded-full border border-zinc-300 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600 transition-colors hover:border-zinc-900 hover:text-zinc-900">
                                <DoorOpen size={12} /> Back to the directory
                            </button>
                        )}
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400" data-testid="store-kicker">
                            {site.standalone ? "Virtual Brick & Mortar" : store.tier_label} · {store.city}
                        </p>
                    </div>
                    <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                        <div className="max-w-3xl">
                            <div className="flex items-center gap-3">
                                {store.logo && (
                                    <img src={mediaUrl(store.logo)} alt="" data-testid="store-logo"
                                        className="h-14 w-14 shrink-0 rounded-2xl border border-zinc-200 bg-white object-contain p-1.5" />
                                )}
                                <h1 className="font-display text-3xl font-extrabold leading-[0.94] tracking-tighter text-zinc-900 sm:text-4xl lg:text-5xl"
                                    data-testid="store-name">
                                    {store.name}
                                </h1>
                            </div>
                            {store.tagline && (
                                <p className="mt-3 font-display text-base font-bold text-zinc-700 md:text-lg" data-testid="store-tagline">{store.tagline}</p>
                            )}
                            <p className="mt-2 max-w-xl text-sm leading-relaxed text-zinc-600" data-testid="store-about">
                                {store.about
                                    || `Everything on this page is printed by ${store.name}. You design it online, ${store.name} prints it, and you pick it up${store.city ? ` in ${store.city}` : ""} or have it shipped.`}
                            </p>
                            <div className="mt-4 flex flex-wrap items-center gap-2">
                                <Chip icon={MapPin} color={color} testid="store-address">{ops.address || store.pickup_address}</Chip>
                                <Chip icon={Clock} color={color} testid="store-hours">{store.hours || ops.pickup_hours}</Chip>
                                <Chip icon={PackageCheck} color={color} testid="store-pickup">
                                    {ops.pickup ? `Pickup · ${ops.pickup_hours || "at the shop"}` : ""}
                                </Chip>
                                <Chip icon={Truck} color={color} testid="store-shipping">{ops.shipping}</Chip>
                                {store.verified && <Chip icon={BadgeCheck} color={color} testid="store-verified">Verified shop</Chip>}
                            </div>
                        </div>
                        <div className="flex flex-col items-start gap-3 lg:items-end">
                            <div className="flex flex-wrap gap-3">
                                <a href="#shop" data-testid="store-shop-cta"
                                    className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold uppercase tracking-wide transition-transform duration-200 hover:scale-[1.03]"
                                    style={{ background: color, color: theme.on }}>
                                    Start an order <ArrowRight size={15} />
                                </a>
                                <Link to={`/artwork-request${q}`} data-testid="store-artwork-cta">
                                    <GhostButton>Send us your art</GhostButton>
                                </Link>
                            </div>
                            <p className="font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: ink }} data-testid="store-promise">
                                {store.designers ? "Live designers. You design it online and this shop prints it." : "Order online and pick it up at the shop."}
                            </p>
                        </div>
                    </div>
                </motion.div>
            </section>

            {billboardsOn && boards.length > 0 && (
                <div data-testid="store-billboards">{boards.map((b) => <Billboard key={b.id} board={b} />)}</div>
            )}

            <ServiceGrid store={store} />

            <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6">
                <StoreReviews data={rev} theme={theme} name={store.name} />
                <MessageShop store={store} />
                <p className="mt-6 text-xs text-zinc-500" data-testid="store-policies-link-row">
                    Pickup, turnaround and refund rules:{" "}
                    <Link to={`/stores/${store.slug}/policies`} className="font-semibold text-zinc-800 underline" data-testid="store-policies-link">
                        {store.name} shop policies
                    </Link>
                </p>
                {directory && (
                    <div className="mt-10 flex flex-col items-start gap-4 border border-zinc-200 bg-zinc-50 p-7 sm:flex-row sm:items-center sm:justify-between" data-testid="store-get-listed">
                        <p className="text-sm text-zinc-700">
                            <span className="font-bold text-zinc-900">Own a print shop?</span> Get a website like this one with your name, your logo and the same live designers.
                        </p>
                        <Link to="/whitelabel"><GhostButton data-testid="store-open-space">See white-label plans</GhostButton></Link>
                    </div>
                )}
            </section>

            {!directory && <PoweredBy store={store} loud={!!site.house} />}
        </>
    );
}
